import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ConferenceTable from '../components/ConferenceTable';
import ConferenceFilterPanel from '../components/ConferenceFilterPanel';
import './ConferencesPage.css';
import { getUserId, getCurrentUser } from '../utils/auth';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import ConferenceCard from '../components/ConferenceCard';
import DatabaseViewMenu from '../components/DatabaseViewMenu';
import { conferenceFiltersService, API, conferenceFavoritesService } from '../services/api';
import Toast from '../components/Toast';
import TrialExpiredOverlay from '../components/TrialExpiredOverlay';
import { useFilters } from '../context/FiltersContext';

/* ------------------ helpers ------------------ */
const toUniqueStringArray = (value) => {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  return value
    .filter((v) => v !== null && v !== undefined && v !== '')
    .map((v) => String(v))
    .filter((v) => (seen.has(v) ? false : (seen.add(v), true)));
};

const sanitizeOptionValues = (values) => {
  const set = new Set();
  (Array.isArray(values) ? values : [])
    .map((value) => {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    })
    .filter((value) => value.length > 0)
    .forEach((value) => set.add(value));
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
};

const UI_STORAGE_KEYS = {
  appliedFilters: 'conferences.appliedFilters'
};

const DENSITY_STORAGE_KEY = 'conferences.density';

const sanitizeConferenceFilters = (raw = {}) => {
  const base = raw && typeof raw === 'object' ? raw : {};

  return {
    Country: toUniqueStringArray(base.Country),
    Price: toUniqueStringArray(base.Price),
    type: toUniqueStringArray(base.type),
    year: toUniqueStringArray(base.year),
    topic: toUniqueStringArray(base.topic),
  };
};

const EMPTY_FILTERS = sanitizeConferenceFilters({});

const areFiltersEmpty = (filters) => {
  if (!filters) {
    return true;
  }
  const {
    Country = [],
    Price = [],
    type = [],
    year = [],
    topic = []
  } = filters;

  return (
    Country.length === 0 &&
    Price.length === 0 &&
    type.length === 0 &&
    year.length === 0 &&
    topic.length === 0
  );
};

const normalizeFiltersForCompare = (filters = {}) => ({
  Country: Array.isArray(filters?.Country) ? filters.Country : [],
  Price: Array.isArray(filters?.Price) ? filters.Price : [],
  type: Array.isArray(filters?.type) ? filters.type : [],
  year: Array.isArray(filters?.year) ? filters.year : [],
  topic: Array.isArray(filters?.topic) ? filters.topic : []
});

const areFiltersEqual = (a, b) => {
  return JSON.stringify(normalizeFiltersForCompare(a)) === JSON.stringify(normalizeFiltersForCompare(b));
};

const buildConferenceSearchParams = (filters) => {
  const params = new URLSearchParams();
  if (!filters) return params;

  const appendArray = (key, values) => {
    if (Array.isArray(values) && values.length > 0) values.forEach((v) => params.append(key, v));
  };

  appendArray('Country', filters.Country);
  appendArray('Price', filters.Price);
  appendArray('type', filters.type);
  appendArray('year', filters.year);
  appendArray('topic', filters.topic);

  return params;
};

/* ------------------ page ------------------ */
const ConferencesPage = ({ viewMode = 'list', onViewModeChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const setHeaderInfo = outletContext.setHeaderInfo;
  const { updateFilterOptions: updateContextFilterOptions, updateActiveFilters: updateContextActiveFilters, registerFilterCallbacks } = useFilters();

  const [conferences, setConferences] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [toast, setToast] = useState(null);
  const [cardViewSortConfig, setCardViewSortConfig] = useState({ key: 'date_debut2', direction: 'ascending' });
  const [favoritesSet, setFavoritesSet] = useState(new Set());
  const [density, setDensity] = useState(() => {
    if (typeof window === 'undefined') {
      return 'detailed';
    }
    const stored = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    return stored === 'compact' ? 'compact' : 'detailed';
  });

  const filtersWrapperRef = useRef(null);
  const [savedFilters, setSavedFilters] = useState([]);
  const [isSavingCurrentFilter, setIsSavingCurrentFilter] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, density);
    }
  }, [density]);

  // url <-> state
  const getFiltersFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return {
      Country: params.getAll('Country'),
      Price: params.getAll('Price'),
      type: params.getAll('type'),
      year: params.getAll('year'),
      topic: params.getAll('topic')
    };
  };

  const [filters, setFilters] = useState(() => {
    const fromUrl = sanitizeConferenceFilters(getFiltersFromUrl());
    if (!areFiltersEmpty(fromUrl)) {
      return fromUrl;
    }

    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem(UI_STORAGE_KEYS.appliedFilters);
        if (stored) {
          const parsed = JSON.parse(stored);
          const sanitizedStored = sanitizeConferenceFilters(parsed);
          if (!areFiltersEmpty(sanitizedStored)) {
            return sanitizedStored;
          }
        }
      } catch (error) {
        console.error('Unable to read saved filters:', error);
      }
    }

    return fromUrl;
  });
  const filtersRef = useRef(filters);

  const [filterOptions, setFilterOptions] = useState({
    Country: [],
    Price: [],
    type: [],
    year: [],
    topic: []
  });

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      if (areFiltersEmpty(filters)) {
        window.localStorage.removeItem(UI_STORAGE_KEYS.appliedFilters);
      } else {
        window.localStorage.setItem(UI_STORAGE_KEYS.appliedFilters, JSON.stringify(filters));
      }
    } catch (error) {
      console.error('Unable to save applied filters:', error);
    }
  }, [filters]);

  const syncFiltersToUrl = useCallback(
    (next) => {
      const params = buildConferenceSearchParams(next);
      navigate({ search: params.toString() }, { replace: true });
    },
    [navigate]
  );

  const refreshData = () => setRefreshTrigger((p) => p + 1);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  /* -------- favorites -------- */
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const response = await conferenceFavoritesService.getAll();
        if (response.success && Array.isArray(response.favorites)) {
          setFavoritesSet(new Set(response.favorites));
        }
      } catch (error) {
        console.error('Error loading conference favorites:', error);
      }
    };
    loadFavorites();
  }, []);

  const handleToggleFavorite = useCallback(async (conferenceId, currentState) => {
    try {
      // Mise à jour optimiste de l'UI
      setFavoritesSet(prev => {
        const newSet = new Set(prev);
        if (currentState) {
          newSet.delete(conferenceId);
        } else {
          newSet.add(conferenceId);
        }
        return newSet;
      });

      // Appel API
      if (currentState) {
        await conferenceFavoritesService.remove(conferenceId);
        showToast('Removed from favorites', 'success');
      } else {
        await conferenceFavoritesService.add(conferenceId);
        showToast('Added to favorites', 'success');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Revenir à l'état précédent en cas d'erreur
      setFavoritesSet(prev => {
        const newSet = new Set(prev);
        if (currentState) {
          newSet.add(conferenceId);
        } else {
          newSet.delete(conferenceId);
        }
        return newSet;
      });
      showToast('Error updating favorites', 'error');
    }
  }, [showToast]);

  /* -------- saved filters -------- */
  const loadSavedFilters = useCallback(async () => {
    try {
      const response = await conferenceFiltersService.getAll();
      if (Array.isArray(response?.filters)) setSavedFilters(response.filters);
      else if (Array.isArray(response)) setSavedFilters(response);
      else setSavedFilters([]);
    } catch (e) {
      console.error('Error loading saved filters:', e);
    }
  }, []);
  useEffect(() => { loadSavedFilters(); }, [loadSavedFilters]);

  const updateFilter = useCallback((filterName, value) => {
    setFilters((currentFilters) => {
      const next = sanitizeConferenceFilters({ ...currentFilters, [filterName]: value });
      syncFiltersToUrl(next);
      return next;
    });
  }, [syncFiltersToUrl]);

  const resetAllFilters = useCallback(() => {
    const empty = sanitizeConferenceFilters({});
    setFilters(empty);
    syncFiltersToUrl(empty);
  }, [syncFiltersToUrl]);

  const handleRemoveFilter = (key, value) => {
    const arr = Array.isArray(filters[key]) ? filters[key].filter((v) => v !== value) : [];
    updateFilter(key, arr);
  };

  const handleSaveCurrentFilters = useCallback(async () => {
    if (isSavingCurrentFilter) return;
    const name = typeof window !== 'undefined' ? window.prompt('Name your filter') : '';
    if (!name || !name.trim()) return;

    setIsSavingCurrentFilter(true);
    try {
      const res = await conferenceFiltersService.save(name.trim(), filters);
      if (res?.success) {
        showToast('Filter saved', 'success');
        await loadSavedFilters();
      } else showToast('Unable to save this filter', 'error');
    } catch (e) {
      console.error('save filter error:', e);
      if (e?.response?.status === 409) showToast('A filter with this name already exists.', 'error');
      else showToast('Error saving filter.', 'error');
    } finally {
      setIsSavingCurrentFilter(false);
    }
  }, [isSavingCurrentFilter, filters, loadSavedFilters, showToast]);

  // Sync filterOptions to FiltersContext
  useEffect(() => {
    updateContextFilterOptions('conferences', filterOptions);
  }, [filterOptions, updateContextFilterOptions]);

  // Sync active filters to FiltersContext
  useEffect(() => {
    updateContextActiveFilters('conferences', filters);
  }, [filters, updateContextActiveFilters]);

  // Update header info with results count and active filters
  useEffect(() => {
    if (!setHeaderInfo) return;

    const resultsText = conferences.length > 0
      ? `Results 1-${conferences.length} of ${conferences.length}`
      : 'No results found';

    const activeFiltersList = [];

    // Map filter keys to their display labels
    const filterLabels = {
      Country: 'Country',
      Price: 'Price',
      type: 'Type',
      year: 'Year',
      topic: 'Topic'
    };

    // Build active filters list
    Object.entries(filters).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          activeFiltersList.push({
            key: `${key}-${value}`,
            filterKey: key,
            label: filterLabels[key] || key,
            value: value
          });
        });
      }
    });

    setHeaderInfo({
      resultsText,
      activeFilters: activeFiltersList
    });

    // Cleanup on unmount
    return () => {
      if (setHeaderInfo) {
        setHeaderInfo({
          resultsText: '',
          activeFilters: []
        });
      }
    };
  }, [conferences, filters, setHeaderInfo]);

  // Register filter callbacks in FiltersContext
  useEffect(() => {
    registerFilterCallbacks('conferences', {
      onFilterChange: updateFilter,
      onResetAllFilters: resetAllFilters,
      onSaveCurrentFilters: handleSaveCurrentFilters,
      savingCurrentFilter: isSavingCurrentFilter,
    });
  }, [registerFilterCallbacks, updateFilter, resetAllFilters, handleSaveCurrentFilters, isSavingCurrentFilter]);

  const handleApplySavedFilter = (doc) => {
    if (!doc) return;
    const payload = doc.filters && typeof doc.filters === 'object' ? doc.filters : doc;
    const sanitized = sanitizeConferenceFilters(payload);
    setFilters(sanitized);
    syncFiltersToUrl(sanitized);
    showToast(doc.name ? `Filter ${doc.name} applied` : 'Filter applied', 'success');
  };

  const handleRequestRemoveSavedFilter = async (doc) => {
    if (!doc) return;
    const ok = typeof window !== 'undefined'
      ? window.confirm(`Delete filter "${doc?.name || 'Unnamed'}"?`)
      : true;
    if (!ok) return;
    try {
      await conferenceFiltersService.remove(doc._id || doc.id);
      showToast('Filter removed', 'success');
      await loadSavedFilters();
    } catch (e) {
      console.error('remove filter error:', e);
      showToast('Unable to remove this filter.', 'error');
    }
  };

  const handleViewMenuChange = (value) => {
    if (value === 'card') {
      if (typeof onViewModeChange === 'function') {
        onViewModeChange('card');
      }
      return;
    }
    const nextDensity = value === 'compact' ? 'compact' : 'detailed';
    setDensity(nextDensity);
    if (typeof onViewModeChange === 'function') {
      onViewModeChange('list');
    }
  };

  const renderSavedFiltersPanel = () => {
    return (
      <div className="conferences-saved-filters-panel">
        <div className="conferences-saved-filters-panel__header">
          {Array.isArray(savedFilters) && savedFilters.length > 0 && (
            <>
              <span className="conferences-saved-filters-panel__title">{`Saved Filters${savedFilters.length ? ` (${savedFilters.length})` : ''}:`}</span>
              <div className="conferences-saved-filters-panel__list">
                {savedFilters.map((doc) => {
                  const key = doc?._id || doc?.id || doc?.name;
                  if (!key) return null;
                  const name = doc?.name || 'Sans nom';
                  const onKey = (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleApplySavedFilter(doc);
                    }
                  };
                  return (
                    <span
                      key={key}
                      className="conferences-filter-chip conferences-saved-filter-chip"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleApplySavedFilter(doc)}
                      onKeyDown={onKey}
                    >
                      <span className="conferences-filter-chip-label">Filter</span>
                      <span className="conferences-filter-chip-value">{name}</span>
                      <button
                        type="button"
                        className="filter-chip-remove conferences-saved-filter-chip__remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRequestRemoveSavedFilter(doc);
                        }}
                        aria-label={`Supprimer le filtre ${name}`}
                      >
                        &times;
                      </button>
                    </span>
                  );
                })}
              </div>
            </>
          )}

          <div className="conferences-saved-filters-panel__actions">
            <div className="conferences-sort">
              <label htmlFor="sort-select" className="conferences-sort-label">Sort by</label>
              <select
                id="sort-select"
                className="conferences-sort-select"
                value={`${cardViewSortConfig.key}:${cardViewSortConfig.direction}`}
                onChange={handleCardViewSortChange}
              >
                <option value="date_debut2:ascending">Soonest</option>
                <option value="date_debut2:descending">Latest</option>
                <option value="name:ascending">Name A-Z</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* -------- filter options (stats) -------- */
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await API.get('/conferences/stats');
        if (response.data?.success) {
          const av = response.data.availableValues || {};
          setFilterOptions({
            Country: sanitizeOptionValues(av.Country),
            Price: sanitizeOptionValues(av.Price),
            type: sanitizeOptionValues(av.type),
            year: sanitizeOptionValues(av.year),
            topic: sanitizeOptionValues(av.topic)
          });
        }
      } catch (err) {
        console.error('Error loading filter options:', err);
      }
    };
    fetchFilterOptions();
  }, []);

  // sync when URL changes
  useEffect(() => {
    const fromUrl = sanitizeConferenceFilters(getFiltersFromUrl());
    const currentFilters = filtersRef.current;

    if (location.search.length === 0 && !areFiltersEmpty(currentFilters)) {
      return;
    }

    if (!areFiltersEqual(currentFilters, fromUrl)) {
      setFilters(fromUrl);
    }
  }, [location.search]);

  /* -------- conferences -------- */
  useEffect(() => {
    const fetchConferences = async () => {
      setIsLoading(true);
      try {
        const params = {};

        if (filters.Country?.length) params.Country = filters.Country;
        if (filters.Price?.length) params.Price = filters.Price;
        if (filters.type?.length) params.type = filters.type;
        if (filters.year?.length) params.year = filters.year;
        if (filters.topic?.length) params.topic = filters.topic;

        const userId = getUserId();
        if (userId) params.userId = userId;
        params._t = Date.now();

        console.log('Fetching conferences with params:', params);
        const response = await API.get('/conferences', { params });
        if (response.data?.success) {
          const conferencesPayload = Array.isArray(response.data.conferences) ? response.data.conferences : [];
          setConferences(conferencesPayload);
          setError(null);
        } else setError('Error loading conferences');
      } catch (err) {
        console.error('Error loading conferences:', err);
        setError('Server connection error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchConferences();
  }, [filters, refreshTrigger]);

  const handleCardViewSortChange = (e) => {
    const [key, direction] = e.target.value.split(':');
    setCardViewSortConfig({ key, direction });
  };

  const sortedConferences = useMemo(() => {
    const sorted = [...conferences].sort((a, b) => {
      const { key, direction } = cardViewSortConfig;
      let aVal = a[key];
      let bVal = b[key];

      if (key === 'date_debut2') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (direction === 'ascending') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return sorted;
  }, [conferences, cardViewSortConfig]);

  /* ------------------ render ------------------ */
  return (
    <div className="conferences-page">
      {/* FiltersSidebar is rendered in DashboardLayout, not here */}

      <div className="conferences-layout">
        <div className="conferences-sticky-container">
          {renderSavedFiltersPanel()}
        </div>

        <section className="conferences-results-section" style={{ position: 'relative' }}>
          <TrialExpiredOverlay section="results" />
          {isLoading ? (
            <div className="loading-indicator">Loading conferences...</div>
          ) : error ? (
            <div className="error-message">
              Error: {error} <button onClick={refreshData}>Retry</button>
            </div>
          ) : (
            <>
              <div className={`card-grid ${sortedConferences.length <= 2 ? 'card-grid-limited' : ''}`}>
                {sortedConferences.length === 0 ? (
                  <div className="no-results">No results found</div>
                ) : (
                  sortedConferences.map((conference) => (
                    <ConferenceCard
                      key={conference._id || conference.id}
                      conference={conference}
                      isAttending={favoritesSet.has(conference._id || conference.id)}
                      onToggleAttendance={(conf, newState) => handleToggleFavorite(conf._id || conf.id, !newState)}
                    />
                  ))
                )}
              </div>
            </>
          )}
          {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </section>
      </div>
    </div>
  );
};

export default ConferencesPage;
